// LusideaContactPage.js - Standalone contact page for Lusidea
import React, { useState } from 'react';
import { Helmet } from 'react-helmet'; // For SEO optimization
import {
  MapPin, Phone, Mail, Send, MessageSquare, Calendar,
  Clock, CheckCircle2, ArrowRight, Globe, Users, Building
} from 'lucide-react';

// Import the standalone components
import HeaderNav from './HeaderNav';
import FooterLan from './FooterLan';
import Button from './Button';

// --- Enhanced Color Palette ---
const COLORS = {
  primary: '#ce2223',       // Lusidea Red
  primaryDark: '#b01a1b',
  primaryLight: '#f8e7e7',  
  primaryLighter: '#fef2f2',
  secondary: '#fdb52a',     // Accent Yellow/Gold
  secondaryDark: '#e09c18',
  secondaryLight: '#fff8eb', 
  secondaryLighter: '#fffbf0',
  black: '#111827',         
  darkGray: '#374151',      
  mediumGray: '#6b7280',    
  lightGray: '#e5e7eb',     
  offWhite: '#f9fafb',      
  white: '#ffffff',
  success: '#10b981',
  successLight: '#ecfdf5'
};

// --- Spacing System ---
const SPACING = {
  xs: 'px-3 py-1.5',
  sm: 'px-4 py-2.5',
  md: 'px-6 py-4',
  lg: 'px-8 py-6',
  xl: 'px-12 py-8',
  '2xl': 'px-16 py-12',
  section: 'py-16 md:py-24 lg:py-28',
  container: 'max-w-7xl mx-auto px-5 sm:px-8 md:px-10 lg:px-12 xl:px-8'
};

// --- SEO Component ---
const SEO = ({ title, description, keywords, image, url, structuredData }) => {
  return (
    <Helmet>
      <title>{title || 'Contact Lusidea - Innovation Consulting & Strategy'}</title>
      <meta name="description" content={description || 'Get in touch with Lusidea\'s innovation experts. Schedule a consultation to discuss how we can help transform your ideas into measurable impact.'} />
      <meta name="keywords" content={keywords || 'innovation consulting, contact, innovation management, future thinking, innovation strategy'} />
      
      {/* Open Graph tags */}
      <meta property="og:title" content={title || 'Contact Lusidea - Innovation Consulting & Strategy'} />
      <meta property="og:description" content={description || 'Get in touch with Lusidea\'s innovation experts. Schedule a consultation to discuss how we can help transform your ideas into measurable impact.'} />
      <meta property="og:image" content={image || '/img/lusidea-og-image.jpg'} />
      <meta property="og:url" content={url || 'https://www.lusidea.com/contact'} />
      <meta property="og:type" content="website" />
      
      {/* Twitter Card tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title || 'Contact Lusidea - Innovation Consulting & Strategy'} />
      <meta name="twitter:description" content={description || 'Get in touch with Lusidea\'s innovation experts. Schedule a consultation to discuss how we can help transform your ideas into measurable impact.'} />
      <meta name="twitter:image" content={image || '/img/lusidea-og-image.jpg'} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={url || 'https://www.lusidea.com/contact'} />
      
      {/* Structured data for rich snippets */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

// --- Toast Notification Component ---
const Toast = ({ message, type = 'success', onClose }) => {
  React.useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 5000);
    
    return () => clearTimeout(timer);
  }, [onClose]);
  
  const bgColor = type === 'success' ? 'bg-successLight' : 'bg-primaryLight';
  const textColor = type === 'success' ? 'text-success' : 'text-primary';
  const icon = type === 'success' ? <CheckCircle2 size={18} /> : <ArrowRight size={18} />;
  
  return (
    <div 
      className={`fixed top-24 right-5 z-50 px-5 py-4 rounded-lg shadow-lg ${bgColor} border border-lightGray/20 max-w-sm flex items-center gap-3`} 
      style={{animation: 'fadeIn 0.3s ease-out'}}
    >
      <span className={textColor}>{icon}</span>
      <p className="text-darkGray font-medium">{message}</p>
      <button 
        onClick={onClose} 
        className="ml-auto text-mediumGray hover:text-primary transition-colors"
        aria-label="Close notification"
      >
        ✕
      </button>
    </div>
  );
};

// Main Contact Page Component
const LusideaContactPage = () => {
  const [toast, setToast] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: '',
    preferredContact: 'email',
    serviceInterest: [],
    howDidYouHear: ''
  });
  
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  
  // Services options
  const serviceOptions = [
    { value: 'innovation-management', label: 'Innovation Management' },
    { value: 'future-thinking', label: 'Future Thinking' },
    { value: 'design-thinking', label: 'Design Thinking' },
    { value: 'strategy-consulting', label: 'Strategy Consulting' },
    { value: 'digital-transformation', label: 'Digital Transformation' },
    { value: 'workshop-facilitation', label: 'Workshop Facilitation' }
  ];
  
  // Handle input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      if (name === 'serviceInterest') {
        setFormData(prevData => {
          const updatedServices = checked 
            ? [...prevData.serviceInterest, value]
            : prevData.serviceInterest.filter(service => service !== value);
          
          return { ...prevData, serviceInterest: updatedServices };
        });
      } else {
        setFormData(prevData => ({ ...prevData, [name]: checked }));
      }
    } else {
      setFormData(prevData => ({ ...prevData, [name]: value }));
    }
    
    // Clear error for this field if it exists
    if (errors[name]) {
      setErrors(prevErrors => ({ ...prevErrors, [name]: '' }));
    }
  };
  
  // Form validation
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    
    if (formData.phone && !/^[+\d\s()-]{7,20}$/.test(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Form submission handler with webhook integration
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    
    try {
      // Prepare data for webhook
      const webhookData = {
        ...formData,
        timestamp: new Date().toISOString(),
        // Convert serviceInterest array to readable string
        serviceInterestString: formData.serviceInterest.length > 0 
          ? serviceOptions
              .filter(option => formData.serviceInterest.includes(option.value))
              .map(option => option.label)
              .join(', ')
          : 'None specified',
        // Add source identifier
        source: 'lusidea-contact-page'
      };

      // Send data to webhook
      await fetch('https://hook.eu2.make.com/fav4o8kusfjga07yzqdu9ld3npzxlp49', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(webhookData)
      });

      console.log("Form submitted and sent to webhook successfully!");
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        subject: '',
        message: '',
        preferredContact: 'email',
        serviceInterest: [],
        howDidYouHear: ''
      });
      
      setFormSubmitted(true);
      
      // Show success toast
      setToast({
        message: 'Your message has been sent successfully! We\'ll be in touch soon.',
        type: 'success'
      });
      
      // Scroll to the top
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
    } catch (error) {
      console.error('Error submitting form:', error);
      
      // Still show success message to user even if webhook fails
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        subject: '',
        message: '',
        preferredContact: 'email',
        serviceInterest: [],
        howDidYouHear: ''
      });
      
      setFormSubmitted(true);
      
      // Show success toast (don't let webhook errors affect user experience)
      setToast({
        message: 'Your message has been sent successfully! We\'ll be in touch soon.',
        type: 'success'
      });
      
      // Scroll to the top
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setLoading(false);
    }
  };
  
  // Schedule options for consultation slots
  const availableSlots = [
    { date: 'Monday, May 20', slots: ['9:00 AM', '11:30 AM', '2:00 PM', '4:30 PM'] },
    { date: 'Tuesday, May 21', slots: ['10:00 AM', '1:00 PM', '3:30 PM'] },
    { date: 'Wednesday, May 22', slots: ['9:30 AM', '11:00 AM', '2:30 PM', '4:00 PM'] },
    { date: 'Thursday, May 23', slots: ['8:30 AM', '10:30 AM', '1:30 PM', '3:00 PM'] },
    { date: 'Friday, May 24', slots: ['9:00 AM', '11:30 AM', '2:00 PM'] }
  ];
  
  // Structured data for organization (for SEO)
  const contactPageStructuredData = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": "Contact Lusidea",
    "description": "Get in touch with Lusidea's innovation experts. Schedule a consultation to discuss how we can help transform your ideas into measurable impact.",
    "url": "https://www.lusidea.com/contact",
    "contactPoint": {
      "@type": "ContactPoint",
      "email": "hello@lusidea.com",
      "contactType": "customer service",
      "areaServed": "Worldwide",
      "availableLanguage": ["English", "Croatian"]
    },
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Ulica Franje Petračića 6",
      "addressLocality": "Zagreb",
      "addressRegion": "",
      "postalCode": "",
      "addressCountry": "HR"
    }
  };

  return (
    <div className="font-sans antialiased text-darkGray bg-offWhite overflow-x-hidden min-h-screen">
      <SEO 
        title="Contact Lusidea - Schedule an Innovation Consultation"
        description="Get in touch with Lusidea's innovation experts. Schedule a consultation to discuss how we can help transform your ideas into measurable impact."
        keywords="innovation consulting, contact, innovation management, future thinking, innovation strategy, schedule consultation"
        structuredData={contactPageStructuredData}
      />
      
      <HeaderNav />
      
      {/* Toast notification system */}
      {toast && (
        <Toast 
          message={toast.message} 
          type={toast.type} 
          onClose={() => setToast(null)} 
        />
      )}
      
      <main>
        {/* Hero Banner with Gradient */}
        <section className="relative pt-28 sm:pt-32 lg:pt-36 pb-16 md:pb-20 lg:pb-24 overflow-hidden">
          {/* Enhanced background gradient with smoother transition */}
          <div className="absolute inset-0 -z-10 bg-gradient-to-br from-primaryLight via-white to-secondaryLight opacity-80"></div>
          
          {/* Subtle abstract shapes for visual interest */}
          <div className="absolute top-20 right-0 w-80 h-80 bg-primary/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-10 w-96 h-96 bg-secondary/5 rounded-full blur-3xl"></div>
          
          <div className={`${SPACING.container} z-10 relative`}>
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight text-black leading-tight mb-6">
                Get in <span className="text-primary">Touch</span> With Our Experts
              </h1>
              
              <p className="text-lg sm:text-xl text-darkGray mb-8 max-w-2xl mx-auto leading-relaxed">
                Ready to transform your innovation capabilities? Schedule a consultation, or reach out through any of our contact channels. We're here to help you turn ideas into measurable impact.
              </p>
              
                              {/* Contact Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-12">
                {/* Email */}
                <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 border border-lightGray/30 flex flex-col items-center text-center">
                  <div className="w-14 h-14 rounded-full bg-primaryLight flex items-center justify-center mb-4">
                    <Mail className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">Email Us</h3>
                  <p className="text-mediumGray mb-4">We'll respond within 24 hours</p>
                  <a 
                    href="mailto:hello@lusidea.com" 
                    className="text-primary font-medium hover:underline"
                  >
                    hello@lusidea.com
                  </a>
                </div>
                
                {/* Address */}
                <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 border border-lightGray/30 flex flex-col items-center text-center">
                  <div className="w-14 h-14 rounded-full bg-secondaryLight flex items-center justify-center mb-4">
                    <MapPin className="w-6 h-6 text-secondaryDark" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">Visit Us</h3>
                  <p className="text-mediumGray mb-4">Lusidea d.o.o.</p>
                  <address className="not-italic text-primary font-medium">
                    Ulica Franje Petračića 6<br />
                    Zagreb, Croatia
                  </address>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        {/* Main Contact Form Section */}
        <section className="py-16 md:py-20 bg-white">
          <div className={SPACING.container}>
            <div className="grid md:grid-cols-12 gap-12 lg:gap-16">
              {/* Left Column - Form */}
              <div className="md:col-span-7">
                <div className="max-w-xl">
                  <div className="mb-8">
                    <div className="inline-block rounded-full bg-primaryLight px-5 py-1.5 mb-4">
                      <h2 className="text-sm font-semibold text-primary uppercase tracking-wide">
                        Let's Connect
                      </h2>
                    </div>
                    <h3 className="text-3xl md:text-4xl font-bold text-black mb-5">
                      Tell Us About Your Innovation Needs
                    </h3>
                    <p className="text-base text-darkGray">
                      Fill out the form below to get started. Our team will review your information and reach out to schedule a personalized consultation.
                    </p>
                  </div>
                  
                  {formSubmitted ? (
                    /* Thank You Message */
                    <div className="bg-successLight p-8 rounded-2xl border border-success/20 text-center">
                      <div className="w-16 h-16 bg-success text-white rounded-full mx-auto flex items-center justify-center mb-6">
                        <CheckCircle2 size={32} />
                      </div>
                      <h4 className="text-2xl font-bold text-black mb-4">Thank You!</h4>
                      <p className="text-darkGray mb-6">
                        Your message has been received. One of our innovation consultants will get back to you within 24 hours to discuss how we can help transform your ideas into reality.
                      </p>
                      <Button 
                        onClick={() => setFormSubmitted(false)} 
                        variant="primary" 
                        size="md" 
                        className="mx-auto"
                      >
                        Send Another Message
                      </Button>
                    </div>
                  ) : (
                    /* Contact Form */
                    <form onSubmit={handleSubmit} className="space-y-6">
                      {/* Name, Email, Phone Row */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        {/* Name */}
                        <div>
                          <label htmlFor="name" className="block text-sm font-medium text-darkGray mb-1">
                            Name <span className="text-primary">*</span>
                          </label>
                          <input
                            type="text"
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            className={`w-full px-4 py-3 bg-offWhite border ${errors.name ? 'border-primary' : 'border-lightGray'} rounded-lg focus:ring-primary focus:border-primary transition-all outline-none`}
                            placeholder="Your full name"
                            required
                          />
                          {errors.name && (
                            <p className="mt-1 text-sm text-primary">{errors.name}</p>
                          )}
                        </div>
                        
                        {/* Email */}
                        <div>
                          <label htmlFor="email" className="block text-sm font-medium text-darkGray mb-1">
                            Email <span className="text-primary">*</span>
                          </label>
                          <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            className={`w-full px-4 py-3 bg-offWhite border ${errors.email ? 'border-primary' : 'border-lightGray'} rounded-lg focus:ring-primary focus:border-primary transition-all outline-none`}
                            placeholder="your.email@company.com"
                            required
                          />
                          {errors.email && (
                            <p className="mt-1 text-sm text-primary">{errors.email}</p>
                          )}
                        </div>
                      </div>
                      
                      {/* Company and Phone */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        {/* Company */}
                        <div>
                          <label htmlFor="company" className="block text-sm font-medium text-darkGray mb-1">
                            Company
                          </label>
                          <input
                            type="text"
                            id="company"
                            name="company"
                            value={formData.company}
                            onChange={handleChange}
                            className="w-full px-4 py-3 bg-offWhite border border-lightGray rounded-lg focus:ring-primary focus:border-primary transition-all outline-none"
                            placeholder="Your organization"
                          />
                        </div>
                        
                        {/* Phone */}
                        <div>
                          <label htmlFor="phone" className="block text-sm font-medium text-darkGray mb-1">
                            Phone
                          </label>
                          <input
                            type="tel"
                            id="phone"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            className={`w-full px-4 py-3 bg-offWhite border ${errors.phone ? 'border-primary' : 'border-lightGray'} rounded-lg focus:ring-primary focus:border-primary transition-all outline-none`}
                            placeholder="+1 (123) 456-7890"
                          />
                          {errors.phone && (
                            <p className="mt-1 text-sm text-primary">{errors.phone}</p>
                          )}
                        </div>
                      </div>
                      
                      {/* Subject */}
                      <div>
                        <label htmlFor="subject" className="block text-sm font-medium text-darkGray mb-1">
                          Subject
                        </label>
                        <input
                          type="text"
                          id="subject"
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                          className="w-full px-4 py-3 bg-offWhite border border-lightGray rounded-lg focus:ring-primary focus:border-primary transition-all outline-none"
                          placeholder="What's this regarding?"
                        />
                      </div>
                      
                      {/* Message */}
                      <div>
                        <label htmlFor="message" className="block text-sm font-medium text-darkGray mb-1">
                          Message <span className="text-primary">*</span>
                        </label>
                        <textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          rows={5}
                          className={`w-full px-4 py-3 bg-offWhite border ${errors.message ? 'border-primary' : 'border-lightGray'} rounded-lg focus:ring-primary focus:border-primary transition-all outline-none`}
                          placeholder="Tell us about your innovation needs and how we can help..."
                          required
                        ></textarea>
                        {errors.message && (
                          <p className="mt-1 text-sm text-primary">{errors.message}</p>
                        )}
                      </div>
                      
                      {/* Preferred Contact Method */}
                      <div>
                        <p className="block text-sm font-medium text-darkGray mb-3">
                          Preferred Contact Method
                        </p>
                        <div className="flex flex-wrap gap-4">
                          <label className="inline-flex items-center">
                            <input 
                              type="radio" 
                              name="preferredContact"
                              value="email"
                              checked={formData.preferredContact === 'email'}
                              onChange={handleChange}
                              className="form-radio text-primary focus:ring-primary h-4 w-4"
                            />
                            <span className="ml-2 text-sm text-darkGray">Email</span>
                          </label>
                          <label className="inline-flex items-center">
                            <input 
                              type="radio" 
                              name="preferredContact"
                              value="phone"
                              checked={formData.preferredContact === 'phone'}
                              onChange={handleChange}
                              className="form-radio text-primary focus:ring-primary h-4 w-4"
                            />
                            <span className="ml-2 text-sm text-darkGray">Phone</span>
                          </label>
                          <label className="inline-flex items-center">
                            <input 
                              type="radio" 
                              name="preferredContact"
                              value="video"
                              checked={formData.preferredContact === 'video'}
                              onChange={handleChange}
                              className="form-radio text-primary focus:ring-primary h-4 w-4"
                            />
                            <span className="ml-2 text-sm text-darkGray">Video Call</span>
                          </label>
                        </div>
                      </div>
                      
                      {/* Services Interest */}
                      <div>
                        <p className="block text-sm font-medium text-darkGray mb-3">
                          What services are you interested in? (Select all that apply)
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                          {serviceOptions.map(option => (
                            <label key={option.value} className="inline-flex items-center">
                              <input 
                                type="checkbox" 
                                name="serviceInterest"
                                value={option.value}
                                checked={formData.serviceInterest.includes(option.value)}
                                onChange={handleChange}
                                className="form-checkbox text-primary focus:ring-primary h-4 w-4"
                              />
                              <span className="ml-2 text-sm text-darkGray">{option.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      
                      {/* How did you hear about us */}
                      <div>
                        <label htmlFor="howDidYouHear" className="block text-sm font-medium text-darkGray mb-1">
                          How did you hear about Lusidea?
                        </label>
                        <select
                          id="howDidYouHear"
                          name="howDidYouHear"
                          value={formData.howDidYouHear}
                          onChange={handleChange}
                          className="w-full px-4 py-3 bg-offWhite border border-lightGray rounded-lg focus:ring-primary focus:border-primary transition-all outline-none"
                        >
                          <option value="">Please select</option>
                          <option value="search">Search Engine</option>
                          <option value="social">Social Media</option>
                          <option value="referral">Referral</option>
                          <option value="event">Event or Conference</option>
                          <option value="article">Article or Publication</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                      
                      {/* Submit Button */}
                      <div className="pt-4">
                        <Button
                          type="submit"
                          variant="primary"
                          size="lg"
                          icon={Send}
                          className="w-full sm:w-auto shadow-md hover:shadow-lg transition-shadow"
                          disabled={loading}
                        >
                          {loading ? 'Sending...' : 'Send Message'}
                        </Button>
                        <p className="mt-4 text-sm text-mediumGray">
                          <span className="text-primary">*</span> Required fields
                        </p>
                      </div>
                    </form>
                  )}
                </div>
              </div>
              
              {/* Right Column - Schedule & Office Info */}
              <div className="md:col-span-5">
                {/* Schedule Consultation with Calendly */}
                <div className="bg-offWhite rounded-2xl p-8 shadow-lg border border-lightGray/30 mb-8">
                  <div className="flex items-center mb-6">
                    <div className="w-12 h-12 rounded-full bg-secondaryLight flex items-center justify-center mr-4">
                      <Calendar className="w-6 h-6 text-secondaryDark" />
                    </div>
                    <h4 className="text-2xl font-bold text-black">Schedule a Call</h4>
                  </div>
                  <p className="text-base text-darkGray mb-6">
                    Book a consultation with one of our innovation experts to discuss your specific challenges and goals. Select a convenient time from our calendar.
                  </p>
                  
                  <a 
                    href="https://calendly.com/lusidea/chat-with-lusidea" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="block w-full bg-secondary hover:bg-secondaryDark text-black font-semibold px-6 py-4 rounded-xl text-center transition-colors duration-300 shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                  >
                    <Calendar size={20} />
                    Schedule on Calendly
                  </a>
                  
                  <p className="text-sm text-mediumGray text-center mt-4">
                    You'll be redirected to our Calendly page to select a convenient time slot
                  </p>
                </div>
                
                {/* Office Location */}
                <div className="bg-gradient-to-br from-primaryLight to-primaryLighter rounded-2xl p-8 shadow-lg border border-lightGray/30">
                  <div className="flex items-center mb-6">
                    <div className="w-12 h-12 rounded-full bg-white/60 flex items-center justify-center mr-4">
                      <Building className="w-6 h-6 text-primary" />
                    </div>
                    <h4 className="text-2xl font-bold text-black">Our Office</h4>
                  </div>
                  
                  <div className="space-y-6">
                    {/* Zagreb Office */}
                    <div className="flex">
                      <MapPin size={20} className="text-primary flex-shrink-0 mt-1 mr-3" />
                      <div>
                        <h5 className="font-semibold text-black mb-1">Lusidea d.o.o.</h5>
                        <address className="not-italic text-darkGray mb-2">
                          Ulica Franje Petračića 6<br />
                          Zagreb, Croatia
                        </address>
                        <a href="mailto:hello@lusidea.com" className="text-primary text-sm hover:underline flex items-center">
                          <Mail size={14} className="mr-1" /> hello@lusidea.com
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        {/* FAQ Section */}
        <section className="py-16 md:py-24 bg-offWhite">
          <div className={SPACING.container}>
            <div className="text-center max-w-3xl mx-auto mb-16">
              <div className="inline-block rounded-full bg-secondaryLight px-5 py-1.5 mb-4">
                <h2 className="text-sm font-semibold text-secondaryDark uppercase tracking-wide">
                  Frequently Asked Questions
                </h2>
              </div>
              <h3 className="text-3xl sm:text-4xl md:text-5xl font-bold text-black mb-6">
                Have <span className="text-secondary">Questions</span>?
              </h3>
              <p className="text-base sm:text-lg text-darkGray max-w-2xl mx-auto leading-relaxed">
                Find answers to commonly asked questions about our innovation consulting services and how we work with clients.
              </p>
            </div>
            
            {/* Two-column FAQ layout */}
            <div className="grid md:grid-cols-2 gap-6 lg:gap-10 max-w-5xl mx-auto">
              <div className="space-y-6">
                {/* FAQ Item 1 */}
                <div className="bg-white p-6 rounded-xl shadow-md border border-lightGray/30">
                  <h4 className="text-xl font-bold text-black mb-3">What types of organizations do you work with?</h4>
                  <p className="text-darkGray">
                    We work with organizations of all sizes across industries, from startups to Fortune 500 companies. Our methodology adapts to your specific context, whether you're in technology, healthcare, finance, manufacturing, or the public sector.
                  </p>
                </div>
                
                {/* FAQ Item 2 */}
                <div className="bg-white p-6 rounded-xl shadow-md border border-lightGray/30">
                  <h4 className="text-xl font-bold text-black mb-3">How long does a typical innovation project take?</h4>
                  <p className="text-darkGray">
                    Project timelines vary based on scope and complexity. Our engagements range from 2-day workshops to 6-month comprehensive innovation programs. We'll work with you to design a timeline that meets your objectives and constraints.
                  </p>
                </div>
                
                {/* FAQ Item 3 */}
                <div className="bg-white p-6 rounded-xl shadow-md border border-lightGray/30">
                  <h4 className="text-xl font-bold text-black mb-3">What is your approach to measuring innovation success?</h4>
                  <p className="text-darkGray">
                    We establish clear metrics aligned with your business goals at the outset of any engagement. These may include innovation pipeline health, conversion rates, time-to-market, ROI, and capability development metrics to ensure tangible impact.
                  </p>
                </div>
              </div>
              
              <div className="space-y-6">
                {/* FAQ Item 4 */}
                <div className="bg-white p-6 rounded-xl shadow-md border border-lightGray/30">
                  <h4 className="text-xl font-bold text-black mb-3">Do you offer virtual consulting services?</h4>
                  <p className="text-darkGray">
                    Yes, we provide both in-person and virtual consulting options. Our digital collaboration tools and virtual workshop methodologies deliver the same high-quality experience and outcomes regardless of your location.
                  </p>
                </div>
                
                {/* FAQ Item 5 */}
                <div className="bg-white p-6 rounded-xl shadow-md border border-lightGray/30">
                  <h4 className="text-xl font-bold text-black mb-3">How do you handle confidentiality and intellectual property?</h4>
                  <p className="text-darkGray">
                    We take confidentiality seriously. We sign NDAs with all clients and have strict protocols for handling sensitive information. Any IP developed during our engagements typically remains the property of our clients.
                  </p>
                </div>
                
                {/* FAQ Item 6 */}
                <div className="bg-white p-6 rounded-xl shadow-md border border-lightGray/30">
                  <h4 className="text-xl font-bold text-black mb-3">Do you offer innovation training for our teams?</h4>
                  <p className="text-darkGray">
                    Absolutely. Knowledge transfer is a core element of our approach. We offer customized training programs that build innovation capabilities within your organization, empowering your teams to drive innovation long after our engagement ends.
                  </p>
                </div>
              </div>
            </div>
            
            {/* More Questions Banner */}
                          <div className="mt-16 text-center">
              <div className="inline-block bg-white px-8 py-6 rounded-2xl shadow-lg border border-lightGray/30">
                <div className="flex items-center justify-center gap-3">
                  <MessageSquare size={24} className="text-primary" />
                  <p className="text-lg font-medium">Still have questions? <a href="mailto:hello@lusidea.com" className="text-primary hover:underline">Email us</a></p>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        {/* CTA Section */}
        <section className="py-16 md:py-24 bg-gradient-to-br from-primaryLight via-white to-secondaryLight relative overflow-hidden">
          {/* Background decoration */}
          <div className="absolute inset-0 opacity-50" 
            style={{
              backgroundImage: 'radial-gradient(#ce2223 1px, transparent 1px), radial-gradient(#fdb52a 1px, transparent 1px)',
              backgroundSize: '40px 40px, 40px 40px',
              backgroundPosition: '0 0, 20px 20px'
            }} 
            aria-hidden="true">
          </div>
          
          <div className={SPACING.container}>
            <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-8 md:p-12 shadow-xl border border-lightGray/30 max-w-4xl mx-auto text-center relative z-10">
              <div className="w-20 h-20 bg-primary rounded-2xl mx-auto flex items-center justify-center mb-6 shadow-lg">
                <Users size={32} className="text-white" />
              </div>
              <h3 className="text-3xl md:text-4xl font-bold text-black mb-6">
                Join Our Innovation Community
              </h3>
              <p className="text-lg text-darkGray mb-8 max-w-2xl mx-auto leading-relaxed">
                Subscribe to our newsletter to receive the latest insights, case studies, and innovation methodologies. Stay ahead of the curve with monthly updates from our experts.
              </p>
              
              <form className="max-w-md mx-auto flex gap-3 mb-6">
                <input
                  type="email"
                  placeholder="Your email address"
                  className="flex-grow px-4 py-3 bg-offWhite border border-lightGray rounded-lg focus:ring-primary focus:border-primary transition-all outline-none"
                  required
                />
                <Button variant="primary" size="md">
                  Subscribe
                </Button>
              </form>
              
              <p className="text-sm text-mediumGray">
                We respect your privacy. Unsubscribe at any time.
              </p>
            </div>
          </div>
        </section>
      </main>
      
      <FooterLan />
    </div>
  );
};

export default LusideaContactPage;